import {
  QueryClient,
  QueryFunction,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'

import { customInstance, ErrorType } from '@/features/auth/axios-config'
import {
  AnalyticsLevel,
  ErrorResponse,
  GetApprovedFilesParams,
  GetAvgCycleTimeParams,
  GetAvgDaysPerFileTrendParams,
  GetCommentDensityParams,
  GetCompletionRateParams,
  GetDsCompletionRateParams,
  GetLateDeliverablesParams,
  GetNoOfProjectsParams,
  GetOpenCommentsParams,
  GetResolutionRateParams,
  GetResolutionRateTrendParams,
  GetReviewCycleTimeParams,
  GetReviewTimeTrendParams,
  GetTflCompletionRateParams,
  GetTimeToFirstCommentParams,
} from '@/schema'

import {
  getApprovedFiles,
  getAvgCycleTime,
  getAvgDaysPerFileTrend,
  getCommentDensity,
  getCompletionRate,
  getDsCompletionRate,
  getLateDeliverables,
  getNoOfProjects,
  getOpenComments,
  getResolutionMetrics,
  getResolutionRate,
  getResolutionRateTrend,
  getReviewCycleTime,
  getReviewTimeTrend,
  getTflCompletionRate,
  getTimeToFirstComment,
  SecondParameter,
} from '../services'

const getQueryKey = <T>(params?: T & { key: string }) => [
  'analytics',
  'metrics',
  ...(params ? [params] : []),
]

/**
 * @summary Get project deliverable statistics
 */
export const useGetNoOfProjects = <
  TData = Awaited<ReturnType<typeof getNoOfProjects>>,
  TError = ErrorType<ErrorResponse>,
>(
  params?: GetNoOfProjectsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getNoOfProjects>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetNoOfProjectsParams>({ ...params, key: 'getNoOfProjects' })

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getNoOfProjects>>> = ({
    signal,
  }) => getNoOfProjects(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get late deliverables with overdue information
 */
export const useGetLateDeliverables = <
  TData = Awaited<ReturnType<typeof getLateDeliverables>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetLateDeliverablesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLateDeliverables>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetLateDeliverablesParams>({
      ...params,
      key: 'getLateDeliverables',
    })

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getLateDeliverables>>
  > = ({ signal }) => getLateDeliverables(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get comment density by project
 */
export const useGetCommentDensity = <
  TData = Awaited<ReturnType<typeof getCommentDensity>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetCommentDensityParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getCommentDensity>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetCommentDensityParams>({
      ...params,
      key: 'getCommentDensity',
    })

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getCommentDensity>>
  > = ({ signal }) => getCommentDensity(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get completion rate metrics
 */
export const useGetCompletionRate = <
  TData = Awaited<ReturnType<typeof getCompletionRate>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetCompletionRateParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getCompletionRate>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetCompletionRateParams>({
      ...params,
      key: 'getCompletionRate',
    })

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getCompletionRate>>
  > = ({ signal }) => getCompletionRate(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get resolution rate metrics
 */
export const useGetResolutionRate = <
  TData = Awaited<ReturnType<typeof getResolutionRate>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetResolutionRateParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getResolutionRate>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetResolutionRateParams>({
      ...params,
      key: 'getResolutionRate',
    })

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getResolutionRate>>
  > = ({ signal }) => getResolutionRate(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get comprehensive comment resolution metrics
 */
export const useGetResolutionMetrics = <
  TData = Awaited<ReturnType<typeof getResolutionMetrics>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetResolutionRateParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getResolutionMetrics>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetResolutionRateParams>({
      ...params,
      key: 'getResolutionMetrics',
    })

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getResolutionMetrics>>
  > = ({ signal }) => getResolutionMetrics(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get resolution metrics at project level
 */
export const useGetResolutionMetricsByProject = <
  TData = Awaited<ReturnType<typeof getResolutionMetrics>>,
  TError = ErrorType<ErrorResponse>,
>(
  projectId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getResolutionMetrics>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const params: GetResolutionRateParams = {
    level: AnalyticsLevel.project,
    project_id: projectId,
  }

  return useGetResolutionMetrics<TData, TError>(params, options, queryClient)
}

/**
 * @summary Get resolution metrics at deliverable set level
 */
export const useGetResolutionMetricsByDeliverableSet = <
  TData = Awaited<ReturnType<typeof getResolutionMetrics>>,
  TError = ErrorType<ErrorResponse>,
>(
  deliverableSetId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getResolutionMetrics>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const params: GetResolutionRateParams = {
    level: AnalyticsLevel.deliverable_set,
    deliverable_set_id: deliverableSetId,
  }

  return useGetResolutionMetrics<TData, TError>(params, options, queryClient)
}

/**
 * @summary Get open comments count metrics
 */
export const useGetOpenComments = <
  TData = Awaited<ReturnType<typeof getOpenComments>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetOpenCommentsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOpenComments>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetOpenCommentsParams>({ ...params, key: 'getOpenComments' })

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOpenComments>>> = ({
    signal,
  }) => getOpenComments(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get resolution rate trend metrics
 */
export const useGetResolutionRateTrend = <
  TData = Awaited<ReturnType<typeof getResolutionRateTrend>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetResolutionRateTrendParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getResolutionRateTrend>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetResolutionRateTrendParams>({
      ...params,
      key: 'getResolutionRateTrend',
    })

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getResolutionRateTrend>>
  > = ({ signal }) => getResolutionRateTrend(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get review time trend metrics
 */
export const useGetReviewTimeTrend = <
  TData = Awaited<ReturnType<typeof getReviewTimeTrend>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetReviewTimeTrendParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReviewTimeTrend>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetReviewTimeTrendParams>({
      ...params,
      key: 'getReviewTimeTrend',
    })

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getReviewTimeTrend>>
  > = ({ signal }) => getReviewTimeTrend(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get weighted average days per file trend over time
 */
export const useGetAvgDaysPerFileTrend = <
  TData = Awaited<ReturnType<typeof getAvgDaysPerFileTrend>>,
  TError = ErrorType<ErrorResponse>,
>(
  params?: GetAvgDaysPerFileTrendParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAvgDaysPerFileTrend>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetAvgDaysPerFileTrendParams>({
      ...params,
      key: 'getAvgDaysPerFileTrend',
    })

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAvgDaysPerFileTrend>>
  > = ({ signal }) => getAvgDaysPerFileTrend(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get average cycle time for a project
 */
export const useGetAvgCycleTime = <
  TData = Awaited<ReturnType<typeof getAvgCycleTime>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetAvgCycleTimeParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getAvgCycleTime>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetAvgCycleTimeParams>({
      ...params,
      key: 'getAvgCycleTime',
    })

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAvgCycleTime>>> = ({
    signal,
  }) => getAvgCycleTime(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get review cycle time for a specific deliverable set
 */
export const useGetReviewCycleTime = <
  TData = Awaited<ReturnType<typeof getReviewCycleTime>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetReviewCycleTimeParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getReviewCycleTime>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetReviewCycleTimeParams>({
      ...params,
      key: 'getReviewCycleTime',
    })

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getReviewCycleTime>>
  > = ({ signal }) => getReviewCycleTime(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get TFL completion rate for a specific deliverable set
 */
export const useGetTflCompletionRate = <
  TData = Awaited<ReturnType<typeof getTflCompletionRate>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetTflCompletionRateParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTflCompletionRate>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetTflCompletionRateParams>({
      ...params,
      key: 'getTflCompletionRate',
    })

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTflCompletionRate>>
  > = ({ signal }) => getTflCompletionRate(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get deliverable set completion rate for a specific project
 */
export const useGetDsCompletionRate = <
  TData = Awaited<ReturnType<typeof getDsCompletionRate>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetDsCompletionRateParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getDsCompletionRate>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetDsCompletionRateParams>({
      ...params,
      key: 'getDsCompletionRate',
    })

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getDsCompletionRate>>
  > = ({ signal }) => getDsCompletionRate(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get approved files count with bucket drill-down
 */
export const useGetApprovedFiles = <
  TData = Awaited<ReturnType<typeof getApprovedFiles>>,
  TError = ErrorType<ErrorResponse>,
>(
  params?: GetApprovedFilesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getApprovedFiles>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetApprovedFilesParams>({
      ...params,
      key: 'getApprovedFiles',
    })

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getApprovedFiles>>
  > = ({ signal }) => getApprovedFiles(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}

/**
 * @summary Get time to first comment for a specific deliverable set
 */
export const useGetTimeToFirstComment = <
  TData = Awaited<ReturnType<typeof getTimeToFirstComment>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetTimeToFirstCommentParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getTimeToFirstComment>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> => {
  const { query: queryOptions, request: requestOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ??
    getQueryKey<GetTimeToFirstCommentParams>({
      ...params,
      key: 'getTimeToFirstComment',
    })

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTimeToFirstComment>>
  > = ({ signal }) => getTimeToFirstComment(params, requestOptions, signal)

  const query = useQuery(
    { queryKey, queryFn, ...queryOptions },
    queryClient
  ) as UseQueryResult<TData, TError>

  return query
}
